/**
 * Admin Layout - Enterprise Dark Theme
 * Main layout for Admin UI with header, sidebar, and content area
 * Visually aligned with IT Operations Dashboard
 */
import React from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { getUserFromToken, stopImpersonation } from '../utils/adminApi';
import { showGlassmorphismWelcome } from '../utils/glassmorphismToasts';
import { ChevronDown, LogOut } from 'lucide-react';
import NotificationBell from './shared/NotificationBell';
import ITSidebar from './it/ITSidebar';
import { SessionHealthIndicator, BreakButton, SystemDowntimeButton } from './session';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import './AdminLayout.css';
import HRSidebarWidgets from './hr/HRSidebarWidgets';
import { getVisibleWorkspaceTabs } from './hr/MyWorkspace';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { alphabetizeAdminNav } from '../lib/adminNavOrder';
import { clearActiveWorkspace } from '../utils/workspaces';

// CLIENT-PRICING-UX-001 — Pricing & Costs navigation (same routes/RBAC; labels make the three concepts unmistakable)
const PRICING_AND_COSTS_ITEMS = [
  { path: '/admin/pricing', label: 'Client Selling Prices', icon: '💵', primary: true },
  { path: '/admin/ground-rates', label: 'Ground Abstractor Costs', icon: '🗺️' },
  { path: '/admin/online-coverage', label: 'Online Search Coverage', icon: '🌐' },
  { path: '/admin/import-history', label: 'Rate Import History', icon: '🕘' }
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const userInfo = getUserFromToken();
    setUser(userInfo);
    setLoading(false);
  }, []);

  // WELCOME-ONCE-001 (Aug 2026): consume the post-login welcome flag here too.
  // Admins land on /admin (this shell) — without this consumer the flag
  // lingered and the "Welcome" toast fired later when they first navigated
  // to the Operations dashboard mid-session.
  React.useEffect(() => {
    if (user && sessionStorage.getItem('showWelcomeBanner') === 'true') {
      sessionStorage.removeItem('showWelcomeBanner');
      setTimeout(() => {
        showGlassmorphismWelcome(user.full_name || user.email, user.role);
      }, 500);
    }
  }, [user]);

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
    } catch (error) {
      alert('Failed to stop impersonation: ' + error.message);
    }
  };

  // P0 SECURITY: Proper logout — clear ALL tokens, queue the unified
  // glassmorphism logout-notice, and SPA-navigate to /login so the
  // toast survives without a full bundle reload.
  const handleLogout = async () => {
    // PREMIUM-NOTIFY-001 — progress toast first; the Login page completes it. Logout proceeds regardless.
    try { require('../utils/glassmorphismToasts').showLogoutInProgress(); } catch (_) { /* toast layer absent */ }
    // /api/auth/logout answers with Clear-Site-Data "storage" (security control, unchanged) which also wipes the
    // cookie-notice acknowledgement; keep that UI preference so the banner is not re-shown after every logout.
    let cookieAck = null;
    try { cookieAck = localStorage.getItem('tf-cookie-notice-ack'); } catch (_) { /* storage unavailable */ }
    // AUTH-HARDENING-001: revoke tokens server-side FIRST (access blacklist +
    // all refresh tokens) so the session cannot be replayed after logout.
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: localStorage.getItem('refresh_token') || undefined }),
        });
      }
    } catch (_) { /* best-effort — local clearing below always runs */ }

    // Clear all auth-related storage
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    try { if (cookieAck) localStorage.setItem('tf-cookie-notice-ack', cookieAck); } catch (_) { /* storage unavailable */ }

    // Clear permissions singleton cache (RBAC isolation between sessions)
    try {
      const { clearPermissionsCache } = require('../hooks/usePermissions');
      clearPermissionsCache();
    } catch (e) { /* hook module not loaded yet */ }
    clearActiveWorkspace(); // WORKSPACE-SWITCHER-001

    // Queue the unified logout notice — Login page will render the
    // glassmorphism toast on mount. queueLogoutNotice writes to
    // localStorage (not sessionStorage) so it survives Chrome's
    // bfcache `pagehide` handler that does sessionStorage.clear()
    // on protected-page exit.
    try {
      const { queueLogoutNotice } = require('../utils/logoutNotice');
      queueLogoutNotice('manual');
    } catch (_) { /* helper not loaded */ }

    // SPA navigation (no full reload) so the logout toast on /login survives.
    // `replace: true` prevents back button from returning to admin pages.
    navigate('/login', { replace: true });
  };

  // Check if user is IT role (IT Manager or IT Support)
  const isITRole = user?.role === 'it_manager' || user?.role === 'it_support';
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // PORTAL-CONTEXT-001: Admin browsing inside the IT Operations Center keeps
  // the IT shell on the shared modules (/admin/team, /admin/workspace) until
  // they explicitly navigate back to an Admin Portal route. Role still
  // controls permissions/data (admin stays org-wide); this is shell-only.
  const IT_CONTEXT_ROUTES = ['/admin/team', '/admin/workspace', '/admin/users'];
  let itPortalFlag = false;
  try { itPortalFlag = sessionStorage.getItem('titleflow-portal-context') === 'it'; } catch (_) { /* noop */ }
  const onITContextRoute = IT_CONTEXT_ROUTES.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  const inITPortalContext = isAdmin && itPortalFlag && onITContextRoute;

  // Auto-clear the context when a non-IT-role user lands on any admin route
  // outside the IT set (Admin Home, HR, Settings, ...) — explicit exit.
  React.useEffect(() => {
    if (isITRole) return;
    const inITArea = location.pathname.startsWith('/admin/it-dashboard') || onITContextRoute;
    if (!inITArea && itPortalFlag) {
      try { sessionStorage.removeItem('titleflow-portal-context'); } catch (_) { /* noop */ }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // HR-PERSONA-001: HR roles see only the HR + Recruitment sidebar sections.
  const isHR = user?.role === 'hr_manager' || user?.role === 'hr_specialist' || user?.role === 'hr';

  // HR-MODULE-CONTEXT-001 (Sep 2026): Admin inside the HR area gets a dedicated HR Portal shell
  // (HR + Recruitment nav, "Back to Admin"). Purely route-derived so refresh / deep links keep it.
  // Shell-only: Admin's RBAC, routes and HR functionality are untouched.
  const HR_CONTEXT_PREFIXES = ['/admin/hr', '/admin/recruitment', '/admin/policies'];
  const inHRPortalContext = isAdmin && HR_CONTEXT_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  
  // Check if user is Jurisdiction Manager
  const isJurisdictionManager = user?.role === 'jurisdiction_manager';

  // Check if user is Jurisdiction Requestor (Session 24)
  const isJurisdictionRequestor = user?.role === 'jurisdiction_requestor';

  // Jul 2026 — accounting reaches /admin/pricing + /admin/products inside
  // the AdminLayout shell; they get a Finance-only sidebar.
  const isAccounting = user?.role === 'accounting';

  // Get role display name
  const getRoleDisplayName = (role) => {
    const roleNames = {
      'admin': 'Administrator',
      'it_manager': 'IT Manager',
      'it_support': 'IT Support',
      'jurisdiction_manager': 'Jurisdiction Manager',
      'jurisdiction_requestor': 'Jurisdiction Requestor',
      'sales_marketing': 'Sales & Marketing'
    };
    return roleNames[role] || role;
  };

  // Get the chip's "name" line.
  //
  // Seed accounts (e.g. itmanager@pnatitle.com) ship with `full_name`
  // literally set to the same human label that `getRoleDisplayName`
  // returns — "IT Manager", "IT Support", "Jurisdiction Manager", etc.
  // When that happens the profile chip would render the *same string
  // twice* (name on top, role underneath) which reads as "the user's
  // name is missing". Detect that collision and fall back to the email
  // prefix so the two chip lines stay visually distinct.
  //   Example:  "IT Manager / IT Manager"  →  "itmanager / IT Manager"
  // Real-human accounts (full_name = "Prashanth G", etc.) are
  // unaffected.
  const getDisplayName = () => {
    if (!user) return 'User';
    const rawName  = (user.full_name || '').trim();
    const roleLabel = getRoleDisplayName(user.role) || '';
    const collides =
      rawName.length > 0 &&
      roleLabel.length > 0 &&
      rawName.toLowerCase() === roleLabel.toLowerCase();
    if (collides) {
      const emailPrefix = user.email?.split('@')[0];
      return emailPrefix || rawName;
    }
    return rawName || user.email?.split('@')[0] || 'User';
  };

  // Get user initials
  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Build navigation items with sections
  const getNavItems = () => {
    const items = [];

    // HR-MODULE-CONTEXT-001: dedicated HR Portal navigation for Admin while inside the HR module.
    // Same targets as the Admin Portal's HR + Recruitment sections (no My Workspace — employee-facing).
    if (inHRPortalContext) {
      return [
        {
          section: 'HR',
          items: [
            { path: '/admin/hr/dashboard', label: 'HR Dashboard', icon: '🏠', primary: true },
            { path: '/admin/hr/attendance-reports', label: 'Attendance Reports', icon: '🕒' },
            { path: '/hr/breaks/history', label: 'Break History', icon: '☕' },
            { path: '/admin/hr/celebrations', label: 'Celebrations', icon: '🎉' },
            { path: '/admin/hr/employees', label: 'Employee Directory', icon: '👤' },
            { path: '/admin/hr/audit-log', label: 'HR Audit Log', icon: '📜' },
            { path: '/admin/hr/leave-registry', label: 'Leave Registry', icon: '📅' },
            { path: '/admin/policies', label: 'Policy Center', icon: '📋' }
          ]
        },
        {
          section: 'Recruitment',
          items: [
            { path: '/admin/recruitment/candidates', label: 'Candidates', icon: '📝' },
            { path: '/admin/recruitment/jobs', label: 'Job Openings', icon: '💼' },
            { path: '/admin/recruitment/kanban', label: 'Pipeline Board', icon: '📊' }
          ]
        }
      ];
    }
    
    // IT Operations Section - Primary for IT roles. May 17, 2026 — added
    // My Workspace nav option per RBAC update so IT roles can access
    // Attendance / Leave / Payslips / Documents alongside their IT Dashboard.
    // May 17, 2026 (UI Theme Consistency) — points to /admin/workspace
    // (nested under AdminLayout) so the dark portal shell stays visible
    // instead of bouncing the user out to the light Dashboard.
    if (isITRole) {
      items.push({ 
        section: 'IT Operations',
        items: [
          { path: '/admin/it-dashboard', label: 'IT Dashboard', icon: '🖥️', primary: true },
          { path: '/admin/workspace', label: 'My Workspace', icon: '👤' }
        ]
      });
    }
    
    // Dashboard - Only for admin
    // ADMIN-HOME-001 (Jun 2026): "Admin Home" returns to the central
    // Admin Dashboard portal; Operations links surface the production
    // modules directly in the admin sidebar.
    if (isAdmin) {
      items.push({ 
        section: 'Overview',
        items: [
          { path: '/admin', label: 'Admin Home', icon: '🏠', primary: true },
          // WORKSPACE-NAV-VIS-001 (Oct 2026): My Workspace hidden for admin / super admin (role-based)
        ]
      });
      items.push({
        section: 'Operations',
        items: [
          { path: '/dashboard', label: 'Operations Dashboard', icon: '🖥️' },
          // NAV-CLEANUP (Sep 2026): duplicate "Orders" (/dashboard/orders) entry removed — same view as
          // Operations Dashboard. The route itself stays (JR matrix access, Order Desk workspace home).
          { path: '/dashboard/typing', label: 'Typing Queue', icon: '⌨️' },
          { path: '/analytics', label: 'Analytics', icon: '📈' }
        ]
      });
      // CRM Phase 1 — Admin entry points. SALES-CRM-NAV-CLEANUP-001: the standalone "Team Activity" link was
      // removed from this sidebar; the /admin/crm route, page and APIs remain (deep links + CRM shell link still work).
      items.push({
        section: 'Sales CRM',
        items: [
          { path: '/admin/crm/performance', label: 'Sales Performance', icon: '🎯' },
          { path: '/crm', label: 'Open CRM Workspace', icon: '🤝' }
        ]
      });
      // TRAINING-WB-001: ephemeral trainer whiteboard (no landing-page card)
      items.push({
        section: 'Training',
        items: [
          { path: '/training/whiteboard?from=admin', label: 'Whiteboard', icon: '🖊️' }
        ]
      });
    }
    
    // Jurisdiction Management — primary landing for jurisdiction_manager,
    // also surfaced to admin (Feb 2026) so the per-user spec's
    // "JURISDICTION MANAGEMENT" group exists in one place instead of
    // duplicated under Master Data.
    if (isAdmin || isJurisdictionManager) {
      items.push({ 
        section: 'Jurisdiction Management',
        items: [
          { path: '/admin/jurisdictions/dashboard', label: 'Jurisdiction Dashboard', icon: '📊', primary: !isAdmin },
          { path: '/admin/jurisdictions', label: 'Jurisdiction Directory', icon: '🏛️' },
          { path: '/admin/jurisdiction-requests', label: 'Jurisdiction Requests', icon: '📥' },
          // JURISDICTION-HEALTH-CLEANUP-001: 'Health Report' entry retired (route + API kept for deep links / dashboard-independent use).
          // Feb 2026 — relocated from the profile dropdown into the
          // Jurisdiction workflow so all jurisdiction-related tools live
          // under one section. Admin + Jurisdiction Manager get write
          // access; the pages enforce read-only for other roles.
          { path: '/admin/jurisdictions/tax-collectors', label: 'PA Tax Collectors', icon: '🏢' },
          { path: '/admin/jurisdictions/tax-categories', label: 'PA Tax Categories', icon: '🏷️' },
          // JURISDICTION-REQUESTS-CONSOLIDATION-001: Collector Corrections now lives in Jurisdiction Requests (?category=corrections); route redirects.
          // PENDING-COLLECTOR-CLEANUP-001 (Option 1): 'Pending Collector Assignments' sidebar entry retired;
          // route /admin/jurisdictions/legacy-fallbacks, page, APIs and tests are retained (Option 2 = future ticket).
          // May 17, 2026 — My Workspace nav option for JM users only
          // (admin has its own Overview/Workspace entry above).
          ...(isJurisdictionManager ? [{ path: '/admin/workspace', label: 'My Workspace', icon: '👤' }] : []),
        ]
      });
    }

    // Jurisdiction Requestor Section — Session 24 (Feb 22, 2026). Can ONLY
    // view the directory read-only + submit/track new-jurisdiction requests.
    if (isJurisdictionRequestor) {
      items.push({
        section: 'Jurisdiction',
        items: [
          { path: '/admin/my-jurisdiction-dashboard', label: 'Dashboard', icon: '📊', primary: true },
          { path: '/admin/jurisdictions', label: 'Directory', icon: '🏛️' },
          { path: '/admin/jurisdiction-requests', label: 'My Requests', icon: '📝' },
          // Feb 2026 — Jurisdiction Requestor now has read-only access to
          // the consolidated Jurisdiction admin tools. Page-level RBAC
          // hides all write actions for this role.
          { path: '/admin/jurisdictions/tax-collectors', label: 'PA Tax Collectors', icon: '🏢' },
          { path: '/admin/jurisdictions/tax-categories', label: 'PA Tax Categories', icon: '🏷️' },
          // JURISDICTION-REQUESTS-CONSOLIDATION-001: Collector Corrections now lives in Jurisdiction Requests (?category=corrections); route redirects.
          // May 17, 2026 — My Workspace nav option (RBAC update).
          // Points to nested /admin/workspace (UI Theme Consistency fix);
          // the App.js JR-guard exception for /dashboard/workspace is no
          // longer relied upon because the new route lives inside /admin.
          { path: '/admin/workspace', label: 'My Workspace', icon: '👤' }
        ]
      });
    }
    
    // Administration Section - Available to Admin and IT Manager
    if (isAdmin || isITRole) {
      const adminItems = [];
      adminItems.push({ path: '/admin/users', label: 'Users', icon: '👥' });
      // Roles & Matrix - Admin only (IT Manager gets permission error)
      if (isAdmin) {
        adminItems.push({ path: '/admin/roles', label: 'Roles & Matrix', icon: '🔐' });
      }
      // Audit Logs — requires audit.view (admin + it_manager only; it_support
      // lacks the permission so the item is hidden — RBAC-LEGACY-FALLBACK).
      if (isAdmin || user?.role === 'it_manager') {
        adminItems.push({ path: '/admin/audit-logs', label: 'Audit Logs', icon: '📋' });
      }
      // LEGACY-PRICING-MIGRATION-001: Pricing Diagnostics removed from user-facing navigation (route kept for internal admin/IT troubleshooting)
      
      items.push({
        section: 'Administration',
        items: adminItems
      });
    }
    
    // HR section — merged HR Records + Break Management per PR 1 final
    // navigation decision. Admin + HR roles; TLs reach the same
    // /hr/breaks/* URLs via the top-bar Team dropdown.
    // HR-HOME-CARDS (Jul 22, 2026): HR Portal users (hr_manager /
    // hr_specialist) get a single "HR Home" anchor — all HR + Recruitment
    // modules are reached via navigation cards on the HR landing page.
    // Admin Portal keeps the full sidebar unchanged.
    if (isHR && !isAdmin) {
      // HR-NAV-CLEANUP-001 (Sep 2026): My Workspace tabs (Leave / IT Support / Policies) move from the
      // white "Navigation" card into the dark sidebar. Derived from the SAME RBAC-filtered tab list
      // MyWorkspace renders (getVisibleWorkspaceTabs) — no new module access for HR roles.
      const wsTabs = getVisibleWorkspaceTabs(user);
      const wsItem = (id, label, icon) => (wsTabs.some(t => t.id === id) ? [{ path: `/admin/workspace?tab=${id}`, label, icon }] : []);
      items.push({
        section: 'HR',
        items: [
          { path: '/admin/hr/dashboard', label: 'HR Dashboard', icon: '🏠' },
          // ATT-REG-003 + HR-ANALYTICS-SCOPE-001: attendance reporting inside
          // the HR Portal shell (hr_manager + hr_specialist).
          { path: '/admin/hr/attendance-reports', label: 'Attendance Reports', icon: '🕒' },
          ...wsItem('leave', 'Leave Management', '📅'),
          // TEAM-SHELL-001: org-wide leave approvals inside the HR Portal shell
          ...(user?.role === 'hr_manager'
            ? [
                { path: '/admin/team', label: 'Team Management', icon: '👥' },
                // TRAINING-WB-001: hr_manager only (not hr / hr_specialist)
                { path: '/training/whiteboard?from=hr', label: 'Training Whiteboard', icon: '🖊️' }
              ]
            : [])
        ]
      });
      const support = wsItem('tickets', 'IT Support', '🎫');
      if (support.length) items.push({ section: 'Support', items: support });
      const company = wsItem('company-policies', 'Policies', '🛡️');
      if (company.length) items.push({ section: 'Company', items: company });
    }
    // ADMIN-NAV-FINANCE-HR-CONSOLIDATION-001 (Oct 2026): the Admin Portal
    // no longer lists HR functions in its sidebar. Admins open the HR
    // Dashboard from Admin Home; once inside /admin/hr/*, the shell switches
    // to the dedicated HR Portal navigation (inHRPortalContext above) which
    // exposes every HR module. Routes + RBAC unchanged; direct URLs still work.
    // HR-role users (isHR && !isAdmin) keep their own HR section above.

    // ADMIN-NAV-RECRUITMENT-CONSOLIDATION-001 (Oct 2026): Recruitment section
    // removed from the Admin sidebar — Candidates / Job Openings / Pipeline Board
    // live in the HR Portal nav (inHRPortalContext) and HR Home cards. Routes/RBAC unchanged.
    
    // Finance tools for the accounting persona (Jul 2026). Without this
    // their sidebar would be empty when they open Client Pricing /
    // Product Master inside the AdminLayout shell.
    if (isAccounting) {
      items.push({
        section: 'Finance',
        items: [
          { path: '/admin/products', label: 'Product Master', icon: '📦' },
          { path: '/admin/billing', label: 'Billing Dashboard', icon: '🧾' }
        ]
      });
      items.push({ section: 'Pricing & Costs', items: PRICING_AND_COSTS_ITEMS });
    }

    // Master Data - Only for admin. Feb 2026 — `Product Master` and
    // `Client Pricing` relocated here from the profile dropdown so all
    // system master data sits in one section. Jurisdiction-related
    // entries were removed from this section to avoid duplicating the
    // dedicated Jurisdiction Management group above.
    if (isAdmin) {
      items.push({ 
        section: 'Master Data',
        items: [
          { path: '/admin/client-codes', label: 'Client Codes', icon: '🏷️' },
          { path: '/admin/contract-mappings', label: 'Contract Mappings', icon: '🔗' },
          { path: '/admin/products', label: 'Product Master', icon: '📦' }
        ]
      });
      // CLIENT-PRICING-UX-001 — the three pricing concepts grouped, business-facing labels
      items.push({ section: 'Pricing & Costs', items: PRICING_AND_COSTS_ITEMS });

      // ADMIN-NAV-FINANCE-HR-CONSOLIDATION-001 (Oct 2026): Finance section
      // (Billing Dashboard) removed from the Admin sidebar — reached via the
      // "Finance Dashboard" card on Admin Home (/admin/billing). Accounting
      // persona keeps its own Finance section above. Route/RBAC unchanged.
    }
    
    // System Settings - Only for admin
    if (isAdmin) {
      items.push({ 
        section: 'System Settings',
        items: [
          { path: '/admin/settings/email', label: 'Email Settings', icon: '📧' },
          { path: '/admin/settings/billing', label: 'Billing Settings', icon: '🧾' },
          { path: '/admin/settings/session', label: 'Session Settings', icon: '⏱️' },
          { path: '/admin/settings/qc', label: 'QC Settings', icon: '✅' },
          { path: '/admin/settings/quick-chips', label: 'Quick Reason Chips', icon: '⚡' },
          { path: '/admin/settings/team', label: 'Team Management', icon: '👥' }
        ]
      });
    }
    
    // ADMIN-NAV-AZ-001 (Sep 2026): Admin Portal only — Overview (Admin Home) stays pinned
    // first; remaining groups A→Z and items within each group A→Z. Presentation order only:
    // items are the already-RBAC-filtered ones above (no visibility/route/icon change),
    // and copies are sorted so shared arrays are never mutated. Other personas keep their
    // purpose-built order (primary landing item first).
    return isAdmin ? alphabetizeAdminNav(items) : items;
  };

  const navItems = getNavItems();

  // Feb 27, 2026 — Sidebar active-state bug: clicking "Jurisdiction
  // Dashboard" (/admin/jurisdictions/dashboard) was highlighting both
  // Dashboard AND Directory because the old `startsWith(path)` check
  // matched both `/admin/jurisdictions/dashboard` (correct) and
  // `/admin/jurisdictions` (wrong — shorter prefix of the same path).
  //
  // Fix: longest-prefix-wins. Any nav item whose path is a strict
  // prefix of ANOTHER nav item's path requires an exact pathname match.
  // Other items keep the prefix-match behavior so /admin/users/123
  // still highlights "Users" etc.
  const allPaths = navItems.flatMap((s) => (s.items || []).map((i) => i.path));
  const requiresExactMatch = new Set(
    allPaths.filter((p) =>
      allPaths.some((other) => other !== p && other.startsWith(p + '/'))
    )
  );

  const isActivePath = (path) => {
    const pathname = location.pathname;
    if (path === '/admin') {
      return pathname === '/admin';
    }
    // HR-NAV-CLEANUP-001: tab-scoped items (/admin/workspace?tab=leave) are active only for their own tab
    if (path.includes('?tab=')) {
      const [base, qs] = path.split('?');
      return pathname === base && new URLSearchParams(location.search).get('tab') === new URLSearchParams(qs).get('tab');
    }
    if (requiresExactMatch.has(path)) {
      // Treat exact match OR exact match + trailing slash as active.
      return pathname === path || pathname === path + '/';
    }
    return pathname === path || pathname.startsWith(path + '/');
  };

  // Get header title and subtitle based on context (role-aware branding)
  const getHeaderContent = () => {
    if (isITRole || inITPortalContext) {
      return {
        title: 'IT Operations Center',
        subtitle: 'Real-time IT monitoring & incident control'
      };
    }
    if (isHR && !isAdmin) {
      return {
        title: 'TitleFlow HR',
        subtitle: 'Human resources management'
      };
    }
    return {
      title: 'TitleFlow Admin',
      subtitle: 'System administration & management'
    };
  };

  const headerContent = getHeaderContent();

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-layout">
      {/* Header */}
      <header className="admin-header" data-testid="admin-header">
        <div className="admin-header-left">
          <h1 className="admin-title">{headerContent.title}</h1>
          <span className="admin-subtitle">{headerContent.subtitle}</span>
        </div>
        
        <div className="admin-header-right">
          {/* Impersonation indicator */}
          {user?.isImpersonating && (
            <div className="impersonation-badge">
              <span className="impersonation-label">
                Impersonating: {user.actorEmail || user.actorId}
              </span>
              <button 
                className="btn-stop-impersonation"
                onClick={handleStopImpersonation}
                title="Stop impersonation"
              >
                Stop
              </button>
            </div>
          )}

          {/* Session controls (Jun 12, 2026) — Active status, Break and
              Downtime. IT-UNIFIED-NAV (Jun 2026): IT roles now included —
              the IT Dashboard's embedded header copies were removed when
              the module nav moved into the single unified sidebar. */}
          {user && (
            <div className="admin-session-controls" data-testid="admin-session-controls">
              <SessionHealthIndicator />
              <BreakButton />
              <SystemDowntimeButton />
            </div>
          )}

          {/* Notification bell — Feb 2026 rush ticket: real-time leave
              notifications for IT/HR/Jurisdiction roles. Bell handles its
              own SSE + polling fallback + toast + OS popup. */}
          {/* WORKSPACE-SWITCHER-001 — renders only for multi-workspace users */}
          {user && <WorkspaceSwitcher user={user} />}

          {user && <NotificationBell user={user} />}
          
          {/* Profile Chip with Dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <div
                className="profile-chip"
                data-testid="profile-chip"
              >
                <div className="profile-chip-avatar" data-testid="profile-chip-avatar">
                  {getUserInitials()}
                </div>
                <div className="profile-chip-text">
                  <span className="profile-chip-name" data-testid="profile-chip-name">
                    {getDisplayName()}
                  </span>
                  <span className="profile-chip-role" data-testid="profile-chip-role">
                    {getRoleDisplayName(user?.role)}
                  </span>
                </div>
                <ChevronDown className="profile-chip-caret" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="profile-dropdown-content"
            >
              <DropdownMenuLabel className="profile-dropdown-label">
                <span className="profile-dropdown-label-name">{getDisplayName()}</span>
                <span className="profile-dropdown-label-email">{user?.email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="profile-dropdown-separator" />
              <DropdownMenuItem
                className="profile-dropdown-item profile-dropdown-item-danger"
                onClick={handleLogout}
                data-testid="profile-menu-logout"
              >
                <LogOut className="profile-dropdown-item-icon" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="admin-body">
        {/* IT-UNIFIED-NAV: IT roles get the single unified IT sidebar on every
            admin route (Users / Audit Logs / My Workspace); the IT Dashboard
            page renders its own copy, so it's excluded here. Legacy admin
            sidebar retained for all other personas. */}
        {location.pathname.startsWith('/admin/it-dashboard') ? null : (isITRole || inITPortalContext) ? (
          <ITSidebar isITManager={user?.role === 'it_manager' || inITPortalContext} standalone />
        ) : !(isAdmin && (location.pathname === '/admin' || location.pathname === '/admin/')) && (
        <aside className="admin-sidebar" data-testid="admin-sidebar">
          {/* Brand */}
          <div className="sidebar-brand">
            <div className="brand-logo">
              <div className="brand-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </div>
              <div className="brand-text">
                <span className="brand-name">TitleFlow</span>
                <span className="brand-subtitle">{isITRole ? 'IT Portal' : ((isHR && !isAdmin) || inHRPortalContext) ? 'HR Portal' : 'Admin Portal'}</span>
              </div>
            </div>
          </div>
          {inHRPortalContext && (
            <button
              type="button"
              className="sidebar-back-link"
              data-testid="hr-back-to-admin"
              onClick={() => navigate('/admin')}
            >
              <span aria-hidden="true">←</span> Back to Admin
            </button>
          )}
          
          {/* Navigation */}
          <nav className="admin-nav" data-testid="admin-navigation">
            {navItems.map((section, index) => (
              <div key={`section-${index}`} className="nav-section">
                <div className="nav-section-header">{section.section}</div>
                {section.items.map((item) => (
                  <button
                    key={item.path}
                    className={`nav-item ${isActivePath(item.path) ? 'active' : ''} ${item.primary ? 'primary' : ''}`}
                    onClick={() => navigate(item.path)}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && <span className="nav-badge">{item.badge}</span>}
                  </button>
                ))}
              </div>
            ))}
            {/* HR-PORTAL-SPLIT (Jul 22, 2026) / HR-NAV-CLEANUP-001: compact Insights +
                Upcoming Events widgets after the last HR Portal section */}
            {isHR && !isAdmin && <HRSidebarWidgets />}
          </nav>
        </aside>
        )}

        {/* Main Content */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
